import React, { useEffect, useState } from "react";
import { API, Auth } from "aws-amplify";
import Loadingmini from "./loadingmini";
import { money } from "./staffpage";

function cellKey(cell) {
  if (cell.columnKey) return cell.columnKey + ":" + cell.days;
  return cell.days + ":" + cell.tierNum;
}

function insuranceKey(cell) {
  return cell.miles + ":" + cell.safety;
}

function rangeText(cell) {
  const personal = money(cell.personalPerDay);
  const gig = money(cell.gigPerDay);
  if (!personal) return "—";
  if (personal === gig) return personal;
  return personal + "–" + gig;
}

function priceSide(cell) {
  if (!cell.marked || cell.driverPerDay == null) return null;
  const low = Number(cell.personalPerDay);
  const high = Number(cell.gigPerDay);
  const price = Number(cell.driverPerDay);
  if (!Number.isFinite(price)) return null;
  if (!Number.isFinite(low) || !Number.isFinite(high) || high <= low) return "only";
  return price <= (low + high) / 2 ? "low" : "high";
}

function Matrix({ columns, rows, cells, selectedKey, onSelect, keyFor, labelFor, priceSideFor, rowAxis }) {
  return (
    <div className="rate-grid" style={{ gridTemplateColumns: "8.5rem repeat(" + columns.length + ", minmax(5.5rem, 1fr))" }}>
      <div className="rate-axis">{rowAxis || ""}</div>
      {columns.map((column) => (
        <div className={"rate-head" + (column.caption ? " open-res" : "")} key={column.key || column.tierNum}>
          <div>{column.name || column.label}</div>
          {column.caption ? <div className="rate-head-sub">{column.caption}</div> : null}
        </div>
      ))}
      {rows.map((row) => (
        <React.Fragment key={row.key || row.days}>
          <div className="rate-head">{row.label}</div>
          {columns.map((column) => {
            const cell = cells.find((item) => (
              row.days
                ? item.days === row.days && (column.key ? item.columnKey === column.key : item.tierNum === column.tierNum)
                : item.miles === row.key && item.safety === column.key
            ));
            if (!cell) return <div key={column.key || column.tierNum} />;
            const key = keyFor(cell);
            const classes = ["rate-cell"];
            if (column.caption) classes.push("open-res");
            if (cell.marked) classes.push("driver");
            if (key === selectedKey) classes.push("on");
            const side = priceSideFor ? priceSideFor(cell) : null;
            return (
              <button type="button" key={key} className={classes.join(" ")} onClick={() => onSelect(key)}>
                {side == null ? null : <span className={"rate-price " + side} />}
                <span className="rate-label">{labelFor(cell)}</span>
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function DriverRateCard({ fleetCode, driverId, reloadKey }) {
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [rentalKey, setRentalKey] = useState("");
  const [insuranceKeySelected, setInsuranceKeySelected] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const session = await Auth.currentSession();
        const body = await API.get("adminWebTools", "/ago", { queryStringParameters: {
          back_end: process.env.REACT_APP_BACKEND,
          jwt: session.getAccessToken().getJwtToken(),
          api_endpoint: "api3:portal:driver_rate_card",
          request_type: "GET",
          fleet_code: fleetCode,
          driverId: String(driverId),
        }});
        if (cancelled) return;
        if (!body || body.status !== "success") {
          setError((body && body.message) || "Could not price this driver.");
          setCard(null);
          return;
        }
        setCard(body);
        const rentalCells = (body.rental && body.rental.cells) || [];
        setRentalKey(rentalCells[0] ? cellKey(rentalCells[0]) : "");
        const insuranceCells = (body.insurance && body.insurance.cells) || [];
        const marked = insuranceCells.find((cell) => cell.marked);
        setInsuranceKeySelected(marked ? insuranceKey(marked) : (insuranceCells[0] ? insuranceKey(insuranceCells[0]) : ""));
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (fleetCode && driverId) load();
    return () => { cancelled = true; };
  }, [fleetCode, driverId, reloadKey]);

  if (loading) return <Loadingmini />;
  if (error) return <p className="staff-note">{error}</p>;
  if (!card || !card.rental) return null;
  const rental = card.rental;
  const rentalCell = (rental.cells || []).find((cell) => cellKey(cell) === rentalKey) || rental.cells[0];
  const insurance = card.insurance;
  const insuranceCell = insurance && ((insurance.cells || []).find((cell) => insuranceKey(cell) === insuranceKeySelected) || insurance.cells[0]);

  return (
    <section className="rate-card">
      <h2>Rental prices</h2>
      <p className="rate-note">Before tax, for this driver on the fleet base vehicle. The shaded cell is the one explained.</p>
      <div className="rate-layout">
        <Matrix
          columns={rental.classes}
          rows={rental.windows}
          cells={rental.cells}
          selectedKey={rentalCell ? cellKey(rentalCell) : ""}
          onSelect={setRentalKey}
          keyFor={cellKey}
          labelFor={(cell) => money(cell.grossPrice) || "—"}
        />
        {rentalCell ? (
          <div className="rate-detail">
            {rentalCell.lines.map((line) => <p key={line}>{line}</p>)}
          </div>
        ) : null}
      </div>
      {insurance ? (
        <>
          <h2>Insurance if they buy it</h2>
          <p className="rate-note">Applied miles are miles driven that are not on an approved gig platform. Each cell is the personal price, then personal + gig when those differ. The driver's cell stays shaded. The darker half is the price they would be charged, and it stays put when you click another cell.</p>
          <div className="rate-layout">
            <Matrix
              columns={insurance.safety}
              rows={insurance.miles}
              cells={insurance.cells}
              selectedKey={insuranceCell ? insuranceKey(insuranceCell) : ""}
              onSelect={setInsuranceKeySelected}
              keyFor={insuranceKey}
              labelFor={rangeText}
              priceSideFor={priceSide}
              rowAxis="Applied miles per day"
            />
            {insuranceCell ? (
              <div className="rate-detail">
                {insuranceCell.lines.map((line) => <p key={line}>{line}</p>)}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
      {(card.drivingRecord || []).length ? (
        <div className="rate-detail">
          <h2>Driving record used for this price</h2>
          {card.drivingRecord.map((line) => <p key={line}>{line}</p>)}
        </div>
      ) : null}
    </section>
  );
}
